import React, { useEffect, useState } from "react";
import AppHeader from "../components/Header/AppHeader";
import { apiWrapWithErrorWithData } from "../utilities/apiHelpers";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Modal,
  PasswordInput,
  Radio,
  ScrollArea,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Select,
} from "@mantine/core";
import { IconArrowRight, IconEdit, IconPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  addMasterUser,
  getBuList,
  getModuleList,
  getUserFromMaster,
  getUserRoleList,
  updateMasterUser,
  getDepartmentList,
} from "../utilities/apis/users";
import { validateEmail } from "../utilities/utilities";
import { showNotification } from "@mantine/notifications";

const MODULES = [
  {
    key: "agreement",
    id: "1",
    label: "Agreement",
    name: "agreement",
  },
  {
    key: "recovery",
    id: "2",
    label: "Recovery",
    name: "recovery",
  },
  {
    key: "obligation",
    id: "3",
    label: "Obligation",
    name: "obligation",
  },
];

const TeamManager = () => {
  const AppConfig = window.AppConfig;
  const [userData, setUserData] = useState({
    loading: true,
    users: [],
  });
  const [editId, setEditId] = useState(0);
  function getModuleId(name) {
    if (name === "agreement") {
      return 1;
    } else if (name === "recovery") {
      return 2;
    } else if (name === "obligation") {
      return 3;
    } else if (name === "document") {
      return 4;
    }
  }
  const deptRequiredUserRoles = [17, 18, 19];
  const [buData, setBuData] = useState([]);
  const [userRoleData, setUserRoleData] = useState([]);
  const [moduleList, setModuleList] = useState([]);
  const [roleGroups, setRoleGroups] = useState({});
  const [departmentData, setDepartmentData] = useState([]);

  const emptyUser = {
    id: "",
    name: "",
    email: "",
    phone: "1212121212",
    password: "",
    confirmPassword: "",
    mapOrganization: "1",
    mapBusinessunit: "1",
    enabled: true,
    department: "",
    modules: {
      agreement: {
        enabled: false,
        roles: [],
      },
      recovery: {
        enabled: false,
        roles: [],
      },
      obligation: {
        enabled: false,
        roles: [],
      },
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      // Modules
      const moduleResponse = await apiWrapWithErrorWithData(getModuleList({}));

      if (moduleResponse.success) {
        const formattedModules = moduleResponse.result.map(({ id, name }) => ({
          value: id,
          label: name,
        }));

        setModuleList(formattedModules);
      }

      // Departments
      const departmentResponse =
        await apiWrapWithErrorWithData(getDepartmentList());

      if (departmentResponse.success) {
        const formattedDepartments = departmentResponse.result.map(
          ({ id, name }) => ({
            value: String(id),
            label: name,
          }),
        );

        setDepartmentData(formattedDepartments);
      }

      // Business Units
      const buResponse = await apiWrapWithErrorWithData(getBuList());

      if (buResponse.success) {
        const formattedBU = buResponse.result.map(({ id, name }) => ({
          value: String(id),
          label: name,
        }));

        setBuData(formattedBU);
      }

      // Roles
      const roleResponse = await apiWrapWithErrorWithData(getUserRoleList());

      if (roleResponse.success) {
        const formattedRoles = roleResponse.result.map((userRole) => ({
          value: userRole.id.toString(),
          label: userRole.moduleName + " => " + userRole.name,
        }));

        setUserRoleData(formattedRoles);

        const roleMap = roleResponse.result.reduce((acc, role) => {
          const module = role.moduleName.toLowerCase();

          if (!acc[module]) {
            acc[module] = [];
          }

          acc[module].push({
            value: role.id.toString(),
            label: role.name,
          });

          return acc;
        }, {});

        setRoleGroups(roleMap);
      }
    };

    fetchData();
  }, []);

  const getUsers = async () => {
    const response = await apiWrapWithErrorWithData(getUserFromMaster());

    if (response.success) {
      setUserData({
        loading: false,
        users: response.result.userList,
      });
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const toggleModule = (module, enabled) => {
    setAddUserData((prev) => {
      const updated = {
        ...prev,
        userData: {
          ...prev.userData,
          modules: {
            ...prev.userData.modules,
            [module]: {
              enabled,
              roles: enabled ? prev.userData.modules[module].roles : [],
            },
          },
        },
      };

      if (module === "obligation" && !enabled) {
        updated.userData.department = "";
      }

      return updated;
    });
  };

  const updateRoles = (module, role, checked) => {
    setAddUserData((prev) => {
      const roles = prev.userData.modules[module].roles;

      return {
        ...prev,
        userData: {
          ...prev.userData,
          modules: {
            ...prev.userData.modules,
            [module]: {
              ...prev.userData.modules[module],
              roles: checked
                ? [...roles, role]
                : roles.filter((r) => r !== role),
            },
          },
        },
      };
    });
  };

  const [addUserData, setAddUserData] = useState({
    refresh: true,
    showModal: false,
    userData: emptyUser,
    loadinButton: false,
  });

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const isValidUserRoles = (allowedModules, userRoles) => {
    return allowedModules.every((module) =>
      userRoles.some((role) => roleGroups[module]?.includes(role)),
    );
  };

  const handleClose = () => {
    setEditId(0);
    setErrors({});
    setAddUserData((prev) => ({
      ...prev,
      showModal: false,
      userData: emptyUser,
    }));
  };

  const shouldShowDepartment = (userData) => {
    return userData.modules?.obligation?.enabled || !!userData.department;
  };

  function validate() {
    let error = {};

    if (!addUserData.userData.name.trim()) {
      error.name = "Please enter name";
    }

    if (!validateEmail(addUserData.userData.email)) {
      error.email = "Please enter a valid email";
    }

    if (!editId && addUserData.userData.password.length < 8) {
      error.password = "Minimum 8 characters";
    }

    if (
      !editId &&
      addUserData.userData.password !== addUserData.userData.confirmPassword
    ) {
      error.confirmPassword = "Passwords do not match";
    }

    const enabledModules = Object.values(addUserData.userData.modules).filter(
      (m) => m.enabled,
    );

    if (!enabledModules.length) {
      error.modules = "Select at least one module";
    }

    const invalidModules = [];

    Object.entries(addUserData.userData.modules).forEach(([key, module]) => {
      if (module.enabled && module.roles.length === 0) {
        invalidModules.push(key.charAt(0).toUpperCase() + key.slice(1));
      }
    });

    if (invalidModules.length) {
      error.modules = `${invalidModules.join(", ")} modules require at least one role enabled.`;
    }

    if (
      addUserData.userData.modules.obligation.enabled &&
      !addUserData.userData.department
    ) {
      error.department = "Please select department";
    }

    setErrors(error);

    return Object.keys(error).length === 0;
  }

  const setUpdateUserData = (user) => {
    console.log("Selected User:", user);
    console.log("Department:", user.department);
    console.log("Department Data:", departmentData);

    const moduleIds = user.userModuleIds
      ? user.userModuleIds.split(",").map((id) => id.trim())
      : [];

    const roleIds = user.userRoleIds
      ? user.userRoleIds.split(",").map((id) => id.trim())
      : [];

    const modules = {
      agreement: {
        enabled: moduleIds.includes("1"),
        roles: [],
      },
      recovery: {
        enabled: moduleIds.includes("2"),
        roles: [],
      },
      obligation: {
        enabled: moduleIds.includes("3"),
        roles: [],
      },
    };

    Object.keys(modules).forEach((moduleKey) => {
      const moduleRoles = roleGroups[moduleKey] || [];
      const roleIds =
        user.userRoleIds
          ?.split(",")
          .map((id) => id.trim())
          .filter(Boolean) || [];

      modules[moduleKey].roles = moduleRoles
        .filter((role) => roleIds.includes(String(role.value)))
        .map((role) => String(role.value));
    });

    setAddUserData((prev) => ({
      ...prev,
      showModal: true,
      userData: {
        ...emptyUser,
        id: user.id || "",
        name: user.name || "",
        email: (user.email || "").toLowerCase(),
        phone: user.phone || "0000000000",
        mapOrganization: String(user.mapOrganization || "1"),
        mapBusinessunit: String(
          user.mapBusinessunit || buData?.[0]?.value || "1",
        ),
        enabled: user.enabled === 1 || user.enabled === true,
        department: user.department ? String(user.department) : "",
        modules,
      },
    }));
  };

  const showDepartment = shouldShowDepartment(addUserData.userData);

  console.log("add userData: ", addUserData.userData);
  console.log("errors: ", errors);

  return (
    <div>
      <AppHeader hide={true} />
      <hr></hr>
      <div className="m-8">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-heading">Team Manager</h1>
            <p className="dashboard-subtitle">
              Manage users, roles and business units
            </p>
          </div>
        </div>
        <div className="flex justify-end mb-8">
          <Group gap="md">
            <Button
              variant="light"
              radius="md"
              onClick={() => navigate("/team-manager/bu-list")}
            >
              <IconArrowRight size={20} />
              <span style={{ marginLeft: "8px" }}>Business Units</span>
            </Button>

            <Button
              radius="md"
              onClick={() => {
                setEditId(0);

                setErrors({});

                setAddUserData((prev) => ({
                  ...prev,
                  showModal: true,
                  userData: emptyUser,
                }));
              }}
            >
              <IconPlus size={20} />
              <span style={{ marginLeft: "8px" }}>Add User</span>
            </Button>
          </Group>
        </div>

        {!userData.loading && userData.users.length === 0 && (
          <div className="text-center py-16 text-gray-500">No users found.</div>
        )}

        {!userData.loading && (
          <Card shadow="sm" radius="xl" p="lg" withBorder>
            <div style={{ overflowX: "auto" }}>
              <Table
                striped
                highlightOnHover
                verticalSpacing="md"
                horizontalSpacing="lg"
              >
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Modules</th>
                    <th>Roles</th>
                    <th>Business Unit</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userData.users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td
                        style={{
                          textTransform: "lowercase",
                        }}
                      >
                        {user.email}
                      </td>
                      <td style={{ maxWidth: 250 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            flexWrap: "nowrap",
                            overflowX: "auto",
                            paddingBottom: 4,
                          }}
                        >
                          {user.userModule.split(",").map((module, index) => (
                            <Badge
                              key={`${user.id}-${module}-${index}`}
                              variant="light"
                              color="blue"
                            >
                              {module.trim()}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td style={{ maxWidth: 500 }}>
                        <ScrollArea
                          type="hover"
                          scrollbarSize={5}
                          offsetScrollbars
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              flexWrap: "nowrap",
                              width: "max-content",
                              paddingBottom: 6,
                            }}
                          >
                            {user.userRole.split(",").map((role, index) => {
                              const trimmedRole = role.trim();
                              const isLong = trimmedRole.length > 12;
                              const displayText =
                                trimmedRole.length > 15
                                  ? `${trimmedRole.substring(0, 15)}...`
                                  : trimmedRole;

                              return (
                                <Badge
                                  key={`${user.id}-${role}-${index}`}
                                  title={isLong ? trimmedRole : ""}
                                  color="teal"
                                  variant="outline"
                                  style={{
                                    flexShrink: 0,
                                  }}
                                >
                                  {displayText}
                                </Badge>
                              );
                            })}{" "}
                          </div>
                        </ScrollArea>
                      </td>
                      <td>
                        <Badge color="orange" variant="light">
                          {user.userBusinessunit}
                        </Badge>
                      </td>
                      <td align="center">
                        <ActionIcon
                          color="blue"
                          variant="light"
                          radius="xl"
                          size="lg"
                          onClick={() => {
                            if (Object.keys(roleGroups).length === 0) {
                              showNotification({
                                color: "yellow",
                                title: "Loading",
                                message: "Roles are still loading.",
                              });
                              return;
                            }

                            setEditId(user.id);
                            setTimeout(() => {
                              setUpdateUserData(user);
                            }, 0);
                          }}
                        >
                          <IconEdit size={18} />
                        </ActionIcon>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      <Modal
        title={
          <Stack gap={2}>
            <Text fw={700} size="xl">
              {editId ? "Update User" : "Add User"}
            </Text>

            <Text size="sm" c="dimmed">
              {editId
                ? "Update user details and permissions"
                : "Create a new user and assign permissions"}
            </Text>
          </Stack>
        }
        opened={addUserData.showModal}
        onClose={handleClose}
        size={1150}
        radius="xl"
        centered
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <Stack gap="xl" pb="md">
          <Stack gap="xl">
            <SimpleGrid
              cols={editId ? 2 : 2}
              spacing="lg"
              breakpoints={[{ maxWidth: "sm", cols: 1 }]}
            >
              <TextInput
                radius="md"
                size="md"
                error={errors?.name}
                required
                label="Name"
                placeholder="Enter Name"
                value={addUserData.userData.name}
                onChange={(e) =>
                  setAddUserData({
                    ...addUserData,
                    userData: {
                      ...addUserData.userData,
                      name: e.target.value,
                    },
                  })
                }
              />
              <TextInput
                radius="md"
                size="md"
                error={errors?.email}
                required
                label="Email"
                placeholder="Enter Email"
                type="email"
                value={addUserData.userData.email}
                onChange={(e) =>
                  setAddUserData({
                    ...addUserData,
                    userData: {
                      ...addUserData.userData,
                      email: e.target.value.toLowerCase(),
                    },
                  })
                }
              />
            </SimpleGrid>

            {!editId && (
              <SimpleGrid
                cols={editId ? 2 : 2}
                spacing="lg"
                breakpoints={[{ maxWidth: "sm", cols: 1 }]}
              >
                <PasswordInput
                  label="Password"
                  value={addUserData.userData.password}
                  error={errors.password}
                  placeholder="Enter Password"
                  onChange={(e) =>
                    setAddUserData({
                      ...addUserData,
                      userData: {
                        ...addUserData.userData,
                        password: e.target.value,
                      },
                    })
                  }
                />

                <PasswordInput
                  label="Confirm Password"
                  value={addUserData.userData.confirmPassword}
                  error={errors.confirmPassword}
                  placeholder="Confirm Password"
                  onChange={(e) =>
                    setAddUserData({
                      ...addUserData,
                      userData: {
                        ...addUserData.userData,
                        confirmPassword: e.target.value,
                      },
                    })
                  }
                />
              </SimpleGrid>
            )}
            <Stack gap="xs">
              <Group justify="space-between">
                <Text fw={700} size="lg">
                  Modules & Permissions
                </Text>
              </Group>

              {errors.modules && (
                <Text color="red" size="sm" fw={500} mt={4}>
                  {errors.modules}
                </Text>
              )}
              <SimpleGrid
                cols={3}
                spacing="lg"
                breakpoints={[
                  { maxWidth: "lg", cols: 2 },
                  { maxWidth: "sm", cols: 1 },
                ]}
              >
                {MODULES.map((module) => {
                  const moduleState = addUserData.userData.modules[module.key];
                  const isEnabled = AppConfig?.modules?.[module.name.toLowerCase()] === true;

                  return (
                      <Card
                      key={module.key}
                      withBorder
                      radius="xl"
                      shadow="sm"
                      p="md"
                      style={{
                        transition: "all .2s",
                        borderColor: moduleState.enabled
                          ? "#228be6"
                          : "#e9ecef",
                        display: isEnabled? "block" : "none"
                      }}
                    >
                      <Group justify="space-between" mb="md">
                        <Text fw={600}>{module.label}</Text>

                        <Switch
                          checked={moduleState.enabled}
                          onChange={(e) =>
                            toggleModule(module.key, e.currentTarget.checked)
                          }
                        />
                      </Group>

                      <Divider mb="md" />

                      <ScrollArea h={200} scrollbarSize={5} offsetScrollbars>
                        <Stack gap={8}>
                          {(roleGroups?.[module.key] || []).map((role) => (
                            <Checkbox
                              key={role.value}
                              label={role.label}
                              disabled={!moduleState.enabled}
                              checked={moduleState.roles.includes(role.value)}
                              onChange={(e) =>
                                updateRoles(
                                  module.key,
                                  role.value,
                                  e.currentTarget.checked,
                                )
                              }
                            />
                          ))}
                        </Stack>
                      </ScrollArea>
                    </Card> 
                  );
                })}
              </SimpleGrid>
            </Stack>

            <Card withBorder radius="lg" p="lg" shadow="xs">
              <Text fw={600} mb="md">
                Business Unit
              </Text>

              <SimpleGrid cols={2}>
                {buData.map((bu) => (
                  <Card
                    key={bu.value}
                    withBorder
                    radius="md"
                    p="sm"
                    style={{
                      cursor: "pointer",
                      border:
                        addUserData.userData.mapBusinessunit === bu.value
                          ? "2px solid #228be6"
                          : undefined,
                    }}
                    onClick={() =>
                      setAddUserData((prev) => ({
                        ...prev,
                        userData: {
                          ...prev.userData,
                          mapBusinessunit: bu.value,
                        },
                      }))
                    }
                  >
                    <Radio
                      checked={
                        addUserData.userData.mapBusinessunit === bu.value
                      }
                      label={bu.label}
                      readOnly
                    />
                  </Card>
                ))}
              </SimpleGrid>
            </Card>

            {showDepartment && (
              <div>
                <Select
                  searchable
                  clearable
                  radius="md"
                  size="md"
                  label="Department"
                  placeholder="Select department"
                  value={addUserData.userData.department || null}
                  data={departmentData}
                  error={errors.department || null}
                  onChange={(val) =>
                    setAddUserData((prev) => ({
                      ...prev,
                      userData: {
                        ...prev.userData,
                        department: val || "",
                      },
                    }))
                  }
                />
              </div>
            )}

            <Card withBorder radius="lg" p="md" shadow="xs">
              <Group position="apart">
                <div>
                  <Text fw={600}>Status</Text>
                  <Text size="sm" c="dimmed">
                    Enable user account
                  </Text>
                </div>

                <Switch
                  checked={addUserData.userData.enabled}
                  onChange={(e) =>
                    setAddUserData({
                      ...addUserData,
                      userData: {
                        ...addUserData.userData,
                        enabled: e.currentTarget.checked,
                      },
                    })
                  }
                />
              </Group>
            </Card>
          </Stack>

          <Group position="right" mt="xl">
            <Button
              onClick={async () => {
                if (!validate()) return;

                const isEdit = editId > 0;
                const apiFn = isEdit ? updateMasterUser : addMasterUser;
                const actionLabel = isEdit ? "update" : "add";

                const payload = {
                  ...addUserData.userData,
                  department: addUserData.userData.department
                    ? Number(addUserData.userData.department)
                    : null,
                  hasAccessTo: [],
                  userRole: [],
                };

                MODULES.forEach((module) => {
                  const state = payload.modules[module.key];

                  if (state.enabled) {
                    payload.hasAccessTo.push(module.id);

                    payload.userRole.push(...state.roles);
                  }
                });

                const response = await apiWrapWithErrorWithData(
                  apiFn({ userData: payload }),
                );

                if (response.success) {
                  await getUsers();
                  showNotification({
                    message: `user has been ${actionLabel}d`,
                    color: "green",
                    title: `${actionLabel} user`,
                  });

                  if (isEdit) setEditId(0);

                  setAddUserData({
                    ...addUserData,
                    userData: emptyUser,
                    showModal: false,
                  });
                  setUserData((prev) => ({
                    ...prev,
                    refresh: !prev.refresh,
                  }));
                } else {
                  showNotification({
                    message: `error: user ${actionLabel}d`,
                    color: "red",
                    title: `error: ${actionLabel}ing user`,
                  });
                }
              }}
              radius="md"
              size="md"
              px="xl"
              loading={addUserData.loadingButton}
            >
              {editId ? "Update User" : "Add User"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default TeamManager;
